import { MenuItem, TextField } from "@mui/material";

import {
  FEEDBACK_CATEGORY_LABELS,
  FeedbackCategory,
  FeedbackSentiment,
  NEGATIVE_CATEGORIES,
  POSITIVE_CATEGORIES,
} from "@/shared/domain/agentThread";

import {
  FilterPanel,
  OrgFilter,
  SessionIdFilter,
  UserFilter,
  filterInputClassName,
  type FilterOption,
} from "./FilterPanel";

export interface FeedbackFiltersValues {
  sentiment: FeedbackSentiment | "";
  category: FeedbackCategory | "";
  resolved: "any" | "open" | "resolved";
  hasNotes: "any" | "yes" | "no";
  org: FilterOption | null;
  user: FilterOption | null;
  sessionId: string;
  /** Surfaced inside the User dropdown's popup; when true, the user options
   *  exclude Roboto staff and service accounts. See {@link UserFilter} for
   *  the matching rules. */
  customerOnly: boolean;
  /** Per-message error filter: matches feedback rows whose rated message was
   *  ``failed`` or emitted an ``error`` chunk. Server-side; see ``has_errors``
   *  on ``GET /v1/admin/feedback``. */
  hasErrors: "any" | "yes" | "no";
}

interface FeedbackFiltersProps {
  values: FeedbackFiltersValues;
  onChange: (values: FeedbackFiltersValues) => void;
  onSearch: () => void;
  onClear: () => void;
  isLoading: boolean;
  orgOptions: FilterOption[];
  userOptions: FilterOption[];
  orgOptionsLoading: boolean;
  userOptionsLoading: boolean;
}

// Filter dropdown order: negative categories, positive categories, then OTHER.
const NEGATIVE_OPTIONS: ReadonlyArray<FeedbackCategory> = [
  ...NEGATIVE_CATEGORIES,
];
const POSITIVE_OPTIONS: ReadonlyArray<FeedbackCategory> = [
  ...POSITIVE_CATEGORIES,
];
const ALL_CATEGORY_OPTIONS: ReadonlyArray<FeedbackCategory> = [
  ...NEGATIVE_OPTIONS,
  ...POSITIVE_OPTIONS,
  FeedbackCategory.OTHER,
];

/** Categories selectable when ``sentiment`` filter is set, narrowed to the
 *  same set the wizard and backend allow for that sentiment. ``OTHER`` is
 *  always permitted. When sentiment is "All", every category shows. */
function categoryOptionsForSentiment(
  sentiment: FeedbackSentiment | "",
): ReadonlyArray<FeedbackCategory> {
  if (sentiment === FeedbackSentiment.POSITIVE) {
    return [...POSITIVE_OPTIONS, FeedbackCategory.OTHER];
  }
  if (sentiment === FeedbackSentiment.NEGATIVE) {
    return [...NEGATIVE_OPTIONS, FeedbackCategory.OTHER];
  }
  return ALL_CATEGORY_OPTIONS;
}

export function FeedbackFilters({
  values,
  onChange,
  onSearch,
  onClear,
  isLoading,
  orgOptions,
  userOptions,
  orgOptionsLoading,
  userOptionsLoading,
}: FeedbackFiltersProps) {
  return (
    <FilterPanel onSearch={onSearch} onClear={onClear} isLoading={isLoading}>
      <TextField
        className={filterInputClassName}
        select
        label="Sentiment"
        size="small"
        value={values.sentiment}
        onChange={(e) =>
          onChange({
            ...values,
            sentiment: e.target.value as FeedbackSentiment | "",
          })
        }
      >
        <MenuItem value="">All</MenuItem>
        <MenuItem value={FeedbackSentiment.POSITIVE}>Positive</MenuItem>
        <MenuItem value={FeedbackSentiment.NEGATIVE}>Negative</MenuItem>
      </TextField>
      <TextField
        className={filterInputClassName}
        select
        label="Category"
        size="small"
        value={values.category}
        onChange={(e) =>
          onChange({
            ...values,
            category: e.target.value as FeedbackCategory | "",
          })
        }
      >
        <MenuItem value="">All</MenuItem>
        {categoryOptionsForSentiment(values.sentiment).map((category) => (
          <MenuItem key={category} value={category}>
            {FEEDBACK_CATEGORY_LABELS[category]}
          </MenuItem>
        ))}
      </TextField>
      <OrgFilter
        value={values.org}
        onChange={(org) => onChange({ ...values, org })}
        options={orgOptions}
        loading={orgOptionsLoading}
      />
      <UserFilter
        value={values.user}
        onChange={(user) => onChange({ ...values, user })}
        options={userOptions}
        loading={userOptionsLoading}
        customerOnly={values.customerOnly}
        onCustomerOnlyChange={(customerOnly) =>
          onChange({ ...values, customerOnly })
        }
      />
      <SessionIdFilter
        value={values.sessionId}
        onChange={(sessionId) => onChange({ ...values, sessionId })}
      />
      <TextField
        className={filterInputClassName}
        select
        label="Resolved?"
        size="small"
        value={values.resolved}
        onChange={(e) =>
          onChange({
            ...values,
            resolved: e.target.value as FeedbackFiltersValues["resolved"],
          })
        }
      >
        <MenuItem value="any">Any</MenuItem>
        <MenuItem value="open">Open</MenuItem>
        <MenuItem value="resolved">Resolved</MenuItem>
      </TextField>
      <TextField
        className={filterInputClassName}
        select
        label="Has notes?"
        size="small"
        value={values.hasNotes}
        onChange={(e) =>
          onChange({
            ...values,
            hasNotes: e.target.value as FeedbackFiltersValues["hasNotes"],
          })
        }
      >
        <MenuItem value="any">Any</MenuItem>
        <MenuItem value="yes">Yes</MenuItem>
        <MenuItem value="no">No</MenuItem>
      </TextField>
      <TextField
        className={filterInputClassName}
        select
        label="Has errors?"
        size="small"
        value={values.hasErrors}
        onChange={(e) =>
          onChange({
            ...values,
            hasErrors: e.target.value as FeedbackFiltersValues["hasErrors"],
          })
        }
      >
        <MenuItem value="any">Any</MenuItem>
        <MenuItem value="yes">Yes</MenuItem>
        <MenuItem value="no">No</MenuItem>
      </TextField>
    </FilterPanel>
  );
}
